import { useContext, useId, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Box, ClickAwayListener, IconButton, Paper, Popper, Stack, Typography } from '@mui/material';
import { FlowValidationContext } from '../Contexts/FlowValidationContext';
import { useCopyToClipboard } from '../../../../common/hooks/useCopyToClipboard';

export default function ActivityValidationBadge({ activityId }: { activityId: string }) {
	const issues = useContext(FlowValidationContext).get(activityId);
	const [open, setOpen] = useState(false);
	const anchor = useRef<HTMLButtonElement>(null);
	const dialogId = useId();
	const copyToClipboard = useCopyToClipboard();
	if (!issues?.length) {
		return null;
	}
	return (
		<ClickAwayListener onClickAway={() => setOpen(false)}>
			<Box component="span" sx={{ display: 'contents' }} onKeyDown={(event) => {
				if (event.key === 'Escape') {
					setOpen(false);
					event.stopPropagation();
				}
			}}>
			<IconButton
				ref={anchor}
				className="nodrag nopan"
				aria-label={`${issues.length} validation ${issues.length === 1 ? 'issue' : 'issues'} on this activity`}
				aria-expanded={open}
				aria-controls={open ? dialogId : undefined}
				size="small"
				onClick={(event) => { event.stopPropagation(); setOpen(true); }}
				onMouseEnter={() => setOpen(true)}
				onFocus={() => setOpen(true)}
				sx={{ position: 'absolute', top: -14, right: -14, zIndex: 20, width: 28, height: 28, borderRadius: '50%', bgcolor: 'background.paper', color: 'error.main', border: 1, borderColor: 'error.main', p: 0, '&:hover': { bgcolor: 'background.paper' } }}
			>
				<Typography component="span" variant="caption" sx={{ fontWeight: 600, lineHeight: 1 }}>{issues.length}</Typography>
			</IconButton>
			<Popper open={open} anchorEl={anchor.current} placement="right-start" sx={{ zIndex: (theme) => theme.zIndex.tooltip }} modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}>
				<Paper id={dialogId} role="dialog" aria-label="Activity validation issues" className="nodrag nopan nowheel"
					sx={{ width: 440, maxWidth: 'calc(100vw - 32px)', p: 1.5, boxShadow: 8, border: 1, borderColor: 'divider' }}
					onClick={(event) => event.stopPropagation()} onWheel={(event) => event.stopPropagation()}>
					<Stack direction="row" alignItems="center" justifyContent="space-between">
						<Typography variant="subtitle2" color="error.main">{issues.length} validation {issues.length === 1 ? 'issue' : 'issues'}</Typography>
						<Stack direction="row">
							<IconButton size="small" aria-label="Copy validation issues" title="Copy validation issues" onClick={() => void copyToClipboard(
								issues.map((issue) => `${issue.message}\n${issue.code} at ${issue.path}`).join('\n\n'), String,
							)}><ContentCopyIcon fontSize="small" /></IconButton>
							<IconButton size="small" aria-label="Close validation issues" onClick={() => setOpen(false)}><CloseIcon fontSize="small" /></IconButton>
						</Stack>
					</Stack>
					<Typography variant="caption" color="text.secondary">From the last save attempt. Save again after fixing.</Typography>
					<Stack spacing={1} sx={{ mt: 1, maxHeight: 320, overflowY: 'auto' }}>
						{issues.map((issue, index) => (
							<Box key={`${issue.path}:${issue.code}:${index}`}>
								<Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{issue.message}</Typography>
								<Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflowWrap: 'anywhere' }}>{issue.code} at {issue.path}</Typography>
								</Box>
						))}
					</Stack>
				</Paper>
			</Popper>
			</Box>
		</ClickAwayListener>
	);
}
